/*
 * Ryan O'Dowd
 * 2024-05-18
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  adminSaveNewWhatsNewNote,
  apiUpdateWhatsNewNote,
  fetchAdminWhatsNew,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import Checkbox from '@components/common/Checkbox';
import {
  DatePickerInput,
} from 'react-native-paper-dates';
import Dialog from '@components/common/Dialog';
import FormControl from '@components/common/FormControl';
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import Switch from '@components/common/Switch';
import Table from '@components/common/Table';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const WhatsNewTable = () => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_isNewVersionDialogOpen, setIsNewVersionDialogOpen] = useState(false);
  const [_showAll, setShowAll] = useState(false);
  const [_releaseNotesText, setReleaseNotesText] = useState('');
  const [_releaseDate, setReleaseDate] = useState(new Date(Date.now())); // default to today even though it might not be accepted until tomorrow
  const [_appVersionText, setAppVersionText] = useState('');
  const [_linkText, setLinkText] = useState('');
  const [_isIosSupported, setIsIosSupported] = useState(true);
  const [_isAndroidSupported, setIsAndroidSupported] = useState(true);
  const [_isWebSupported, setIsWebSupported] = useState(true);
  const [_isVisible, setIsVisible] = useState(true);
  const [_whatsNewNoteEditId, setWhatsNewNoteEditId] = useState(null);

  useEffect(() => {
    dispatch(fetchAdminWhatsNew());
  }, [dispatch]);

  const whatsNewNotes = hooks.useTypedSelector((state) => state.whatsNewNotes);
  const tableRows = whatsNewNotes.filter((wnn) => _showAll ? true : wnn.is_supported).sort((a, b) => (new Date(b.release_date)).getTime() - (new Date(a.release_date).getTime())).map((wnn) => {
    const cellStyles = wnn.is_visible ? null : styles.invisibleCell;
    return [ // @TODO: should these be cells instead of an array of objects? props are better than attrs
      {
        id: `${wnn.id}_app_version`,
        content: wnn.app_version,
        sortValue: wnn.app_version.split('.').map((n, index) => +n * (1000 ** (2 - index))).reduce((a, b) => a + b, 0),
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_release_date`,
        content: dateFnsFormat(new Date(wnn.release_date.replace('00:00:00', '12:00:00')), 'MMMM d, yyyy'),
        sortValue: new Date(wnn.release_date),
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_platforms`,
        content: [!!wnn.is_ios && <Icon key='apple' name='apple' size={24} color={styles.colors.dialogText} />, !!wnn.is_android && <Icon key='android' name='android' size={24} color={styles.colors.dialogText} />, !!wnn.is_web && <Icon key='web' name='web' size={24} color={styles.colors.dialogText} />].filter(Boolean),
        sortValue: [wnn.is_ios, wnn.is_android, wnn.is_web].filter(Boolean).length, // @TODO: doesn't need to be sortable
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_release_notes`,
        content: <View style={styles.releaseNotesWrapper}>{wnn.release_notes.split('\n').map((rn) => <pre key={rn} style={styles.note}>{rn.trim() ? `* ${rn}` : ' '}</pre>)}</View>, // @TODO: add ability to edit this
        sortValue: 0, // @TODO: doens't need to be sortable
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_link`,
        content: wnn.link ? <a href={wnn.link}>{wnn.link}</a> : '-',
        sortValue: wnn.link,
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_is_visible`,
        content: (
          <Switch
            isChecked={!!wnn.is_visible}
            onValueChange={() => {
              dispatch(apiUpdateWhatsNewNote(wnn.id, {isVisible: !wnn.is_visible}));
            }}
          />
        ),
        sortValue: wnn.is_visible,
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_is_supported`,
        content: (
          <Switch
            isChecked={!!wnn.is_supported}
            onValueChange={() => {
              dispatch(apiUpdateWhatsNewNote(wnn.id, {isSupported: !wnn.is_supported}));
            }}
          />
        ),
        sortValue: wnn.is_supported,
        styles: cellStyles,
      },
      {
        id: `${wnn.id}_edit`,
        content: (
          <IconButton
            icon='pencil'
            iconColor={styles.colors.primary}
            onPress={() => {
              setReleaseNotesText(wnn.release_notes);
              setReleaseDate(new Date(new Date(wnn.release_date).getTime() + 1000 * 60 * 60 * 12)); // add 12 hours for tz issues
              setAppVersionText(wnn.app_version);
              setLinkText(wnn.link);
              setIsIosSupported(!!wnn.is_ios);
              setIsAndroidSupported(!!wnn.is_android);
              setIsWebSupported(!!wnn.is_web);
              setIsVisible(!!wnn.is_visible);
              setIsNewVersionDialogOpen(true);
              setWhatsNewNoteEditId(wnn.id);
            }}
          />
        ),
        styles: cellStyles,
      },
    ];
  });

  return (
    <View style={styles.container}>
      <View style={styles.sectionWrapper}>
        <Checkbox
          isChecked={_showAll}
          color={styles.colors.accent}
          onPress={() => setShowAll((curr) => !curr)}
          label='Show all'
        />
      </View>
      <Table
        tableTitle='Notes'
        open={true}
        headerRowData={[
          {width: 70, id: 'app_version', content: 'App version'},
          {width: 90, id: 'release_date', content: 'Release date', rightAlign: true},
          {width: 90, id: 'platforms', content: 'Platforms', rightAlign: true},
          {width: 270, id: 'release_notes', content: 'Release notes'},
          {width: 120, id: 'link', content: 'Link'},
          {width: 80, id: 'visible', content: 'Visible?', rightAlign: true},
          {width: 95, id: 'supported', content: 'Supported?', rightAlign: true},
          {width: 70, id: 'edit', content: 'Edit', rightAlign: true},
        ]}
        isSortable={true}
        orderByIndex={0}
        rowData={tableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        filtersJsx={
          <Button
            theme={styles.colors.accent}
            onPress={() => {
              setIsNewVersionDialogOpen(true);
              setWhatsNewNoteEditId(null);
              setReleaseNotesText('');
              setReleaseDate(new Date(Date.now() + 1000 * 60 * 60 * 24)); // default to tomorrow
              setAppVersionText('');
              setLinkText('');
              setIsIosSupported(true);
              setIsAndroidSupported(true);
              setIsWebSupported(false);
            }}
          >
            Add release notes
          </Button>
        }
      />

      <Dialog
        visible={_isNewVersionDialogOpen}
        onDismiss={() => setIsNewVersionDialogOpen(false)}
      >
        <Dialog.Title>Add release notes</Dialog.Title>{/* @TODO: i18n */}
        <Dialog.Content style={styles.dialog}>
          <TextInput// @TODO: validate this value; format and min version
            value={_appVersionText}
            mode='outlined'
            onChangeText={(text) => setAppVersionText(text)}
            placeholder='1.2.3'
            label='App version'
          />

          <TextInput
            value={_releaseNotesText}
            mode='outlined'
            onChangeText={(text) => setReleaseNotesText(text)}
            multiline={true}
            numberOfLines={3}
            // @TODO: paper equivalent? minRows={3}
            // @TODO: paper equivalent? maxRows={6}
            placeholder={`Feature one
Bugfix two
Feature three`}
            label='Release notes'
          />
          <TextInput// @TODO: validate this value; format and min version
            value={_linkText}
            mode='outlined'
            onChangeText={(text) => setLinkText(text)}
            placeholder='https://example.com'
            label='Link (optional)'
          />

          <FormControl label='Release date (12:00pm)'>
            <DatePickerInput
              locale='en'
              label='Release date'
              value={_releaseDate}
              onChange={(newValue) => setReleaseDate(newValue)}
              inputMode='start'
            />
          </FormControl>

          <FormControl label='Platforms'>
            <Checkbox
              isChecked={_isIosSupported}
              onPress={() => setIsIosSupported((curr) => !curr)}
              label='iOS'
            />
            <Checkbox
              isChecked={_isAndroidSupported}
              onPress={() => setIsAndroidSupported((curr) => !curr)}
              label='Android'
            />
            <Checkbox
              isChecked={_isWebSupported}
              onPress={() => setIsWebSupported((curr) => !curr)}
              label='Web'
            />
          </FormControl>
          <FormControl label='Visible to users?'>
            <Checkbox
              isChecked={_isVisible}
              onPress={() => setIsVisible((curr) => !curr)}
              label='Visible'
            />
          </FormControl>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='text'
            onPress={() => setIsNewVersionDialogOpen(false)}
          >
            {i18n('cancel')}
          </Button>
          <Button
            theme={styles.colors.accent}
            onPress={() => {
              if (!_releaseNotesText || !_releaseDate || !_appVersionText || (!_isIosSupported && !_isAndroidSupported && !_isWebSupported)) {
                alert('Please fill required fields.');
                return;
              }
              if (_whatsNewNoteEditId) {
                dispatch(apiUpdateWhatsNewNote(_whatsNewNoteEditId, {
                  releaseNotes: _releaseNotesText,
                  releaseDate: _releaseDate,
                  appVersion: _appVersionText,
                  isIos: _isIosSupported,
                  isAndroid: _isAndroidSupported,
                  isWeb: _isWebSupported,
                  isVisible: _isVisible,
                  link: _linkText,
                }));
              } else {
                dispatch(adminSaveNewWhatsNewNote(_releaseNotesText, _releaseDate, _appVersionText, _isIosSupported, _isAndroidSupported, _isWebSupported, _linkText, _isVisible));
              }
              setIsNewVersionDialogOpen(false);
            }}
          >
            Save release notes
          </Button>
        </Dialog.Actions>
      </Dialog>
    </View>
  );
};

export default WhatsNewTable;
